import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { BreadCrumbs } from '../Layout/Header/BreadCrumbs';

import { BoxBodyContainer, TitleHeaderBox } from '@components/Box';
import { BreadcrumbsContainer } from '@components/BreadcrumbsContainer';
import { DynamicRecordFilter } from '@components/Grid';
import { routes } from '@config/routes';
import { loadConfig } from '@utils/configLoader';
import { getAccessCredential } from 'src/auth/utils/inMemoryTokenStore';

const config = await loadConfig();

export const SalesTaxReport = () => {
  const [dataUrlQueryFilter, setDataUrlQueryFilter] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const token = getAccessCredential()?.value;
  const [yearFilter, setYearFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedYear, setSelectedYear] = useState<
    { value: string; text: string }[]
  >([]);
  const [reportTypeFilter, setReportTypeFilter] = useState<string>('');
  const [selectedReportType, setSelectedReportType] = useState<
    { value: string; text: string }[]
  >([]);
  const [reportName, setReportName] = useState<string>('sales-tax-report');
  const [salesReport, setSalesReport] = useState(true);
  const [paymentsReport, setPaymentsReport] = useState(false);

  const iframeUrl = token
    ? `${config.ReportsBaseUrl}?token=${token}&report=${reportName}${dataUrlQueryFilter}`
    : `${config.ReportsBaseUrl}`;

  const yearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => {
      const year = currentYear - i;
      return {
        value: `${year}`,
        text: `${year}`,
      };
    });
    setSelectedYear(years);
  };

  useEffect(() => {
    yearOptions();
  }, []);

  useEffect(() => {
    setSelectedReportType([
      { value: 'Accrual', text: 'Accrual (Paid & Unpaid)' },
      { value: 'Cash Basis', text: 'Cash Basis (Paid)' },
    ]);
  }, []);

  useEffect(() => {
    let tempDataUrlQueryFilter = '';
    if (yearFilter) {
      tempDataUrlQueryFilter += `&yearFilter=${yearFilter}`;
    }
    if (dateFrom) {
      tempDataUrlQueryFilter += `&dateFrom=${dateFrom}`;
    }
    if (dateTo) {
      tempDataUrlQueryFilter += `&dateTo=${dateTo}`;
    }
    if (reportTypeFilter && reportTypeFilter !== 'Accrual') {
      tempDataUrlQueryFilter += `&reportType=${reportTypeFilter}`;
    }
    setDataUrlQueryFilter(tempDataUrlQueryFilter);
  }, [yearFilter, dateFrom, dateTo, reportTypeFilter]);

  useEffect(() => {
    if (yearFilter) {
      const now = new Date();

      const startDate = `${yearFilter}-01-01`;
      const endDate = new Date(
        Number(yearFilter),
        now.getMonth(),
        now.getDate()
      );

      const formatDate = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      setDateFrom(startDate);
      setDateTo(formatDate(endDate));
    } else {
      setDateFrom('');
      setDateTo('');
    }
  }, [yearFilter]);

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    const defaultStartDate = `${currentYear}-01-01`;
    const defaultEndDate = new Date(currentYear, now.getMonth(), now.getDate());

    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const endDateFormatted = formatDate(defaultEndDate);

    if (!yearFilter && !dateFrom && !dateTo) {
      setDataUrlQueryFilter(
        `&yearFilter=${currentYear}&dateFrom=${defaultStartDate}&dateTo=${endDateFormatted}`
      );
    } else {
      let temp = '';
      if (yearFilter) temp += `&yearFilter=${yearFilter}`;
      if (dateFrom) temp += `&dateFrom=${dateFrom}`;
      if (dateTo) temp += `&dateTo=${dateTo}`;
      if (reportTypeFilter) temp += `&reportType=${reportTypeFilter}`;
      setDataUrlQueryFilter(temp);
    }
  }, [yearFilter, dateFrom, dateTo, reportTypeFilter]);

  const salesReportHandler = () => {
    setSalesReport(true);
    setPaymentsReport(false);
    setReportName('sales-tax-report');
  };

  const paymentsReportHandler = () => {
    setPaymentsReport(true);
    setSalesReport(false);
    setReportName('payments-tax-report');
  };

  const baseBreadCrumbs = [{ link: 'Reports', to: '' }];

  const isOnRootPath = location.pathname === routes.RPT_SALESTAXREPORT;

  const breadCrumbLinks = isOnRootPath
    ? baseBreadCrumbs
    : [
        ...baseBreadCrumbs,
        { link: 'Sales Tax Report', to: routes.RPT_SALESTAXREPORT },
      ];

  return (
    <div className="p-4 space-y-4">
      <BoxBodyContainer>
        <BreadcrumbsContainer>
          <div className="lg:text-sm md:text-sm text-xs text-gray-500">
            Reports
          </div>
          <div className="lg:flex md:flex hidden">
            <BreadCrumbs links={breadCrumbLinks} />
          </div>
        </BreadcrumbsContainer>
        <TitleHeaderBox Title="Sales Tax Report" />

        <div className="p-8 bg-white rounded-b-xl space-y-4 rounded-tr-xl">
          {!token && <div>No authentication token available</div>}
          <div className="w-full">
            <DynamicRecordFilter
              label="Date Range"
              filters={[
                {
                  label: 'Year',
                  type: 'select',
                  selection: selectedYear,
                  value: yearFilter,
                  onChange: (e) => {
                    setYearFilter(e.target.value);
                  },
                },
                {
                  label: 'Start Date',
                  type: 'date',
                  value: dateFrom,
                  onChange: (e) => {
                    setDateFrom(e.target.value);
                  },
                },
                {
                  label: 'End Date',
                  type: 'date',
                  value: dateTo,
                  onChange: (e) => {
                    setDateTo(e.target.value);
                  },
                },
                {
                  label: 'Report Type',
                  type: 'select',
                  selection: selectedReportType,
                  value: reportTypeFilter,
                  onChange: (e) => {
                    setReportTypeFilter(e.target.value);
                  },
                },
              ]}
              onFilter={() => {
                setDataUrlQueryFilter(
                  `&yearFilter=${yearFilter}&dateFrom=${dateFrom}&dateTo=${dateTo}&reportType=${reportTypeFilter}`
                );
              }}
              onClear={() => {
                setYearFilter('');
                setDateFrom('');
                setDateTo('');
                setReportTypeFilter('');
              }}
            />
          </div>

          <div className="flex space-x-4">
            <button
              className={`p-2 ${
                salesReport ? 'border-b-2 border-primary-500' : ''
              }`}
              onClick={salesReportHandler}
            >
              Sales & Purchase
            </button>
            <button
              className={`p-2 ${
                paymentsReport ? 'border-b-2 border-primary-500' : ''
              }`}
              onClick={paymentsReportHandler}
            >
              Payments & Balance Owing
            </button>
          </div>

          <iframe
            ref={iframeRef}
            src={iframeUrl}
            width="100%"
            height="800px"
            frameBorder="0"
            title="Sales Tax Report"
          ></iframe>
        </div>
      </BoxBodyContainer>
      <Outlet />
    </div>
  );
};
